import { ref, onMounted, onUnmounted } from 'vue'
import { useGlobalSearch } from '@/composables/useGlobalSearch'
import { useDevMode } from '@/composables/useDevMode'
import { isClient } from '@/composables/useSSR'

export interface KeyboardShortcut {
  id: string
  key: string
  ctrl?: boolean
  shift?: boolean
  alt?: boolean
  description: string
  category: 'navigation' | 'search' | 'general' | 'dev'
  action: () => void
  devOnly?: boolean
}

// Shared singleton state
const shortcuts = ref<Map<string, KeyboardShortcut>>(new Map())
const isHelpModalOpen = ref(false)
let listenerInitialized = false

// Check if user is typing in an input field
function isInputFocused(): boolean {
  if (!isClient) return false
  const active = document.activeElement
  if (!active) return false

  const tagName = active.tagName.toLowerCase()
  if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
    return true
  }

  // Check for contenteditable
  if (active.getAttribute('contenteditable') === 'true') {
    return true
  }

  return false
}

// Generate a key string for the shortcut map
function getShortcutKey(key: string, ctrl?: boolean, shift?: boolean, alt?: boolean): string {
  const parts: string[] = []
  if (ctrl) parts.push('ctrl')
  if (shift) parts.push('shift')
  if (alt) parts.push('alt')
  parts.push(key.toLowerCase())
  return parts.join('+')
}

// Handle keydown events
function handleKeyDown(event: KeyboardEvent): void {
  if (!event.key) return

  // Skip if typing in an input (unless it's Escape)
  if (isInputFocused() && event.key !== 'Escape') {
    return
  }

  const shortcutKey = getShortcutKey(
    event.key,
    event.ctrlKey || event.metaKey,
    event.shiftKey,
    event.altKey
  )

  const shortcut = shortcuts.value.get(shortcutKey)
  if (shortcut) {
    // Skip dev-only shortcuts if not in dev mode
    if (shortcut.devOnly) {
      const { devMode } = useDevMode()
      if (!devMode.value) return
    }
    event.preventDefault()
    shortcut.action()
  }
}

export function useKeyboardShortcuts() {
  const { open: openSearch } = useGlobalSearch()
  const { togglePanel: toggleDevPanel, toggleDebugOverlay } = useDevMode()

  // Register a shortcut
  const register = (shortcut: KeyboardShortcut): void => {
    const key = getShortcutKey(shortcut.key, shortcut.ctrl, shortcut.shift, shortcut.alt)
    shortcuts.value.set(key, shortcut)
  }

  // Unregister a shortcut
  const unregister = (id: string): void => {
    for (const [key, shortcut] of shortcuts.value) {
      if (shortcut.id === id) {
        shortcuts.value.delete(key)
        break
      }
    }
  }

  // Open help modal
  const openHelp = (): void => {
    isHelpModalOpen.value = true
  }

  // Close help modal
  const closeHelp = (): void => {
    isHelpModalOpen.value = false
  }

  // Get all shortcuts for display
  const getShortcuts = (): KeyboardShortcut[] => {
    return Array.from(shortcuts.value.values())
  }

  // Get shortcuts by category
  const getShortcutsByCategory = (category: KeyboardShortcut['category']): KeyboardShortcut[] => {
    return getShortcuts().filter((s) => s.category === category)
  }

  // Initialize default shortcuts and listener
  onMounted(() => {
    if (!isClient) return
    if (!listenerInitialized) {
      // Register default shortcuts
      register({
        id: 'search-slash',
        key: '/',
        description: 'Suche öffnen',
        category: 'search',
        action: openSearch
      })

      register({
        id: 'help',
        key: '?',
        shift: true,
        description: 'Tastenkürzel anzeigen',
        category: 'general',
        action: openHelp
      })

      register({
        id: 'close-help',
        key: 'Escape',
        description: 'Schließen',
        category: 'general',
        action: closeHelp
      })

      // Dev mode shortcuts (only work in dev mode)
      register({
        id: 'dev-panel',
        key: 'd',
        ctrl: true,
        shift: true,
        description: 'Dev Panel öffnen',
        category: 'dev',
        action: toggleDevPanel,
        devOnly: true
      })

      register({
        id: 'debug-overlay',
        key: 'o',
        ctrl: true,
        shift: true,
        description: 'Debug Overlay',
        category: 'dev',
        action: toggleDebugOverlay,
        devOnly: true
      })

      // Add global event listener
      window.addEventListener('keydown', handleKeyDown)
      listenerInitialized = true
    }
  })

  // Cleanup on unmount (singleton - don't remove listener)
  onUnmounted(() => {
    // Keep listener active for singleton pattern
  })

  return {
    shortcuts: shortcuts,
    isHelpModalOpen,
    register,
    unregister,
    openHelp,
    closeHelp,
    getShortcuts,
    getShortcutsByCategory
  }
}

// Format shortcut key for display
export function formatShortcutKey(shortcut: KeyboardShortcut): string {
  const parts: string[] = []
  if (shortcut.ctrl) parts.push('Ctrl')
  if (shortcut.shift) parts.push('Shift')
  if (shortcut.alt) parts.push('Alt')

  // Format the key nicely
  let key = shortcut.key
  if (key === '/') key = '/'
  else if (key === '?') key = '?'
  else if (key === 'Escape') key = 'Esc'
  else if (key === 'ArrowUp') key = '↑'
  else if (key === 'ArrowDown') key = '↓'
  else if (key === 'ArrowLeft') key = '←'
  else if (key === 'ArrowRight') key = '→'
  else if (key === 'Enter') key = '↵'
  else key = key.toUpperCase()

  parts.push(key)
  return parts.join(' + ')
}
